import React, { useEffect, useRef } from "react";

// SASS
import "./Part2.scss";

// COMPONENTS
import Step from "../Step/Step";

// HELPERS
import animate from "../../utils/animate";

// LIBRARIES
import scrollama from "scrollama";

// ASSETS
import germsImg from "../../assets/img/Germs.png";
import noseImg from "../../assets/img/Nose.png";
import mouthImg from "../../assets/img/Mouth.png";
import throatImg from "../../assets/img/Throat.png";
import lungImg from "../../assets/img/Lungs.png";

export default function Part2({ step }) {
  // SCROLLAMA INSTANCE
  const scrolly = scrollama();

  // ANIMATION STEP DATA
  const animationSequence = [
    { stepNumber: "0", height: null, description: "spacer" },
    {
      stepNumber: "1",
      height: null,
      description: "hide hide--1, show reveal--1, reveal--1a",
    },
    {
      stepNumber: "2",
      height: null,
      description: "show reveal--2, reveal--2a",
    },
    {
      stepNumber: "3",
      height: null,
      description: "show reveal--3, reveal--3a",
    },
    {
      stepNumber: "4",
      height: null,
      description: "show reveal--4, reveal--4a",
    },
    { stepNumber: "5", height: null, description: "show reveal--5" },
    { stepNumber: "6", height: null, description: "spacer" },
  ];

  // REFS
  const hide1Ref = useRef(null);
  const reveal1Ref = useRef(null);
  const reveal1aRef = useRef(null);
  const reveal2Ref = useRef(null);
  const reveal2aRef = useRef(null);
  const reveal3Ref = useRef(null);
  const reveal3aRef = useRef(null);
  const reveal4Ref = useRef(null);
  const reveal4aRef = useRef(null);
  const reveal5Ref = useRef(null);

  // RUN WHEN THE COMPONENT MOUNTS
  useEffect(() => {
    scrolly
      .setup({
        step: `.${step}`,
        debug: false,
        threshold: 1,
        progress: true,
      })
      .onStepEnter(({ element, direction }) => {
        const step = element.dataset.step;

        switch (step) {
          case "1": {
            animate.fadeInOnEnter(reveal1Ref.current, direction);
            animate.fadeInOnEnter(reveal1aRef.current, direction);
            break;
          }
          case "2": {
            animate.fadeInOnEnter(reveal2Ref.current, direction);
            animate.fadeInOnEnter(reveal2aRef.current, direction);
            break;
          }
          case "3": {
            animate.fadeInOnEnter(reveal3Ref.current, direction);
            animate.fadeInOnEnter(reveal3aRef.current, direction);
            break;
          }
          case "4": {
            animate.fadeInOnEnter(reveal4Ref.current, direction);
            animate.fadeInOnEnter(reveal4aRef.current, direction);
            break;
          }
          case "5": {
            animate.fadeInOnEnter(reveal5Ref.current, direction);
            break;
          }
          default:
            break;
        }
      })
      .onStepProgress(({ element, progress }) => {
        const step = element.dataset.step;
        switch (step) {
          default:
            break;
        }
      })
      .onStepExit(({ element, direction }) => {
        const step = element.dataset.step;
        switch (step) {
          case "1": {
            animate.fadeOutOnExit(reveal1Ref.current, direction);
            animate.fadeOutOnExit(reveal1aRef.current, direction);
            break;
          }
          case "2": {
            animate.fadeOutOnExit(reveal2Ref.current, direction);
            animate.fadeOutOnExit(reveal2aRef.current, direction);
            break;
          }
          case "3": {
            animate.fadeOutOnExit(reveal3Ref.current, direction);
            animate.fadeOutOnExit(reveal3aRef.current, direction);
            break;
          }
          case "4": {
            animate.fadeOutOnExit(reveal4Ref.current, direction);
            animate.fadeOutOnExit(reveal4aRef.current, direction);
            break;
          }
          case "5": {
            animate.fadeOutOnExit(reveal5Ref.current, direction);
            break;
          }
          default:
            break;
        }
      });
  }, [scrolly, step]);

  // CUSTOM FUNCTIONS

  return (
    <section className="wrapper">
      <div className="sticky-container">
        {/* STICKY */}
        <div className="part2">
          <div className="part2__wrapper">
            {/* <div className="part2__upper">
              <div className="part2__bracket"></div>
              <div className="part2__line"></div>
            </div> */}
            <div className="part2__lower">
              <div className="part2__lower-wrapper">
                <div className="part2__circle">
                  <div
                    className="part2__img part2__img--germs hide hide--1"
                    ref={hide1Ref}
                  >
                    <img src={germsImg} alt="" className="img-fluid" />
                  </div>
                  <div
                    className="part2__img part2__img--nose reveal reveal--1a"
                    ref={reveal1aRef}
                  >
                    <img src={noseImg} alt="" className="img-fluid" />
                  </div>
                  <div
                    className="part2__img part2__img--mouth reveal reveal--2a"
                    ref={reveal2aRef}
                  >
                    <img src={mouthImg} alt="" className="img-fluid" />
                  </div>
                  <div
                    className="part2__img part2__img--throat reveal reveal--3a"
                    ref={reveal3aRef}
                  >
                    <img src={throatImg} alt="" className="img-fluid" />
                  </div>
                  <div
                    className="part2__img part2__img--lungs reveal reveal--4a"
                    ref={reveal4aRef}
                  >
                    <img src={lungImg} alt="" className="img-fluid" />
                  </div>
                </div>
                <div className="part2__text">
                  <p>
                    We often focus on protecting our face with a mask, since
                    germs can travel to these target zones of the{" "}
                    <span className="reveal reveal--1" ref={reveal1Ref}>
                      <span className="orange">nose,</span>{" "}
                    </span>
                    <span className="reveal reveal--2" ref={reveal2Ref}>
                      <span className="orange">mouth,</span>{" "}
                    </span>
                    <span className="reveal reveal--3" ref={reveal3Ref}>
                      <span className="orange">throat, </span>
                    </span>
                    <span className="reveal reveal--4" ref={reveal4Ref}>
                      and <span className="orange">lungs</span>
                    </span>{" "}
                    <span className="reveal reveal--5" ref={reveal5Ref}>
                      to infect us.
                    </span>
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* STEPS */}
      {animationSequence.map(({ stepNumber, height, description, offset }) => {
        return (
          <Step
            stepSection={step}
            stepNumber={stepNumber}
            stepDescription={description}
            height={height}
            key={stepNumber}
            offset={offset}
          />
        );
      })}
    </section>
  );
}
